import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";

const CrmAutomationPanel = () => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  useEffect(() => {
    fetch(`${url}/leads/automation-config`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setConfig)
      .catch(() => setConfig(null));
  }, [url, token]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const res = await fetch(`${url}/leads/automation-config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setConfig(data);
      toast.success("Automation settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => {
    if (!config) return;
    save({ [key]: !config[key] });
  };

  if (!config) {
    return <p className="crm-insights__muted">Loading automations…</p>;
  }

  return (
    <div className="crm-automation">
      <h3 className="crm-integrations__title">CRM automations</h3>
      <p className="crm-insights__muted" style={{ marginBottom: 12 }}>
        Lightweight rules — no workflow builder required.
      </p>

      <label className="crm-automation__row">
        <input
          type="checkbox"
          checked={Boolean(config.auto_assign_enabled)}
          onChange={() => toggle("auto_assign_enabled")}
          disabled={saving}
        />
        <span>
          <strong>Round-robin assign</strong> — new leads auto-assign to staff in rotation
        </span>
      </label>

      <label className="crm-automation__row">
        <input
          type="checkbox"
          checked={Boolean(config.auto_reminder_new_lead)}
          onChange={() => toggle("auto_reminder_new_lead")}
          disabled={saving}
        />
        <span>
          <strong>Reminder on new lead</strong> — follow-up after{" "}
          <select
            value={config.new_lead_reminder_hours || 24}
            onChange={(e) =>
              save({ new_lead_reminder_hours: Number(e.target.value) })
            }
            className="crm-insights__select"
            disabled={saving}
          >
            <option value={4}>4h</option>
            <option value={24}>24h</option>
            <option value={48}>48h</option>
          </select>
        </span>
      </label>

      <label className="crm-automation__row">
        <input
          type="checkbox"
          checked={Boolean(config.auto_reminder_on_pending)}
          onChange={() => toggle("auto_reminder_on_pending")}
          disabled={saving}
        />
        <span>
          <strong>Reminder when Pending</strong> — auto follow-up 48h after stage change
        </span>
      </label>

      <label className="crm-automation__row crm-automation__row--sla">
        <span>
          <strong>First-response SLA</strong> — alert reps if a new lead is not contacted within{" "}
          <select
            value={config.extra?.sla_response_minutes || 15}
            onChange={(e) =>
              save({
                extra: {
                  ...(config.extra || {}),
                  sla_response_minutes: Number(e.target.value),
                },
              })
            }
            className="crm-insights__select"
            disabled={saving}
          >
            <option value={5}>5 min</option>
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={60}>60 min</option>
          </select>
        </span>
      </label>
    </div>
  );
};

export default CrmAutomationPanel;
